import { useCallback, useContext } from 'react'
import { AudioInputSelection, AudioSourceInfo } from '../UserContext'
import { AppContext, AppContextType } from '../AppContext'
import useMediaSelectionActions from '../actions/useMediaSelectionActions'
import { AudioEngineLocalStreamStats } from '../core/AudioEngine'
import CrossPlatformDialog from './components/CrossPlatformDialog'
import { DialogBody, DialogTitle } from '@/components/Dialog'
import DialogActionButtonsSection from './components/DialogActionButtonsSection'
import { Checkbox } from '@/UIComponents2/Checkbox'

export default function AudioEngineSampleRateMismatchDialogController() {
    const { state: appState, dispatch: appDispatch } = useContext(AppContext) as AppContextType

    const onClose = () => {
        appDispatch({
            type: 'update_app_state',
            value: { showAudioEngineSampleRateMismatchDialog: null },
        })
    }

    if (appState.appState.showAudioEngineSampleRateMismatchDialog) {
        const { selection, stats } = appState.appState.showAudioEngineSampleRateMismatchDialog
        return (
            <CrossPlatformDialog
                frameName={`Dialog:AudioEngineSampleRateMismatchDialogController`}
                onDefaultClose={() => onClose()}
            >
                <Content selection={selection} stats={stats} onClose={() => onClose()} />
            </CrossPlatformDialog>
        )
    } else {
        return null
    }
}

function Content({
    selection,
    stats,
    onClose,
}: {
    selection: AudioInputSelection
    stats: AudioEngineLocalStreamStats
    onClose: () => void
}) {
    const { reloadAudioDeviceSelection } = useMediaSelectionActions()

    const onReload = () => {
        reloadAudioDeviceSelection(selection)
    }

    return (
        <div className="w-full">
            <DialogBody>
                <DialogTitle>Sample rate mismatch detected</DialogTitle>

                <div className="pb-3">
                    Device: <span className="text-muse-yellow">{selection.source.displayLabel}</span>
                </div>

                <div className="pb-8">
                    Expected {stats.expectedSampleRate} Hz but received {stats.measuredSampleRate} Hz.
                </div>

                <DontShowAgain mediaSource={selection.source} />
            </DialogBody>

            <DialogActionButtonsSection
                leftButtonText="Reload device"
                rightButtonText="OK"
                onLeftButtonClicked={() => {
                    onReload()
                    onClose()
                }}
                onRightButtonClicked={() => {
                    onClose()
                }}
            />
        </div>
    )
}

function DontShowAgain({ mediaSource }: { mediaSource: AudioSourceInfo }) {
    const { state: appState, dispatch: appDispatch } = useContext(AppContext) as AppContextType

    const isSelected = appState.appSettings.sampleRateMismatchPromptSuppressedLabels[mediaSource.displayLabel] || false

    const onToggle = useCallback(() => {
        const next = { ...appState.appSettings.sampleRateMismatchPromptSuppressedLabels }

        if (isSelected) {
            delete next[mediaSource.displayLabel]
        } else {
            next[mediaSource.displayLabel] = true
        }

        appDispatch({
            type: 'update_app_settings',
            value: { sampleRateMismatchPromptSuppressedLabels: next },
        })
    }, [
        mediaSource.displayLabel,
        isSelected,
        appState.appSettings.sampleRateMismatchPromptSuppressedLabels,
        appDispatch,
    ])

    return <Checkbox checked={isSelected} onChange={onToggle} label={`Don't show again for this device`} />
}
